import type { TCreateSlice } from "@/store";

import type {
    TExportConfig as TTypedExportConfig,
    TAnnualizationType,
    TExtractionType,
} from "@/apis/odin/apiODIN";

// Export config containing the union of the options of the different export formats.
// This is so the options are remembered when switching between export formats.
// The typed export options are available as derived state.
type TExportConfig = {
    exportFormat: "proda" | "compat";
    prodaIncludeDetailedSummary: boolean;
    prodaIncludeSummary: boolean;
    prodaIncludeMappings: boolean;
    includeNonOperating: boolean;
};

type TExportData =
    | {
          tag: "extractions";
          extractions: {
              extractionId: number;
              extractionType: TExtractionType;
          }[];
      }
    | {
          tag: "annualization";
          projectId: number;
          databaseId: number;
          startDate: string;
          endDate: string;
          interval: "month" | "quarter" | "half" | "year";
          annualizationType: TAnnualizationType | null;
          reconciliation?: boolean;
      }
    | null;
type TStateExport = {
    // That is, if the `ExportModal` view is added to the page.
    exportModalIsVisible: boolean;
    exportConfig: TExportConfig;
    exportData: TExportData;
    exportProjectId: number | null;
    exportDatabaseId: number | null;
    nonOperatingAccountsInCoa: boolean;
    subcategoryInCoa: boolean;
};
type TDerivedStateExport = {
    typedExportConfig: (reconciliation?: boolean) => TTypedExportConfig;
};
type TActionsPublicExport = {
    loadExportConfig: ({
        exportConfig,
    }: {
        exportConfig: TTypedExportConfig;
    }) => void;
    updateExportConfig: ({
        exportConfigUpdate,
    }: {
        exportConfigUpdate: Partial<TExportConfig>;
    }) => void;
    showExportModal: ({
        extractions,
        projectId,
        databaseId,
    }: {
        extractions: {
            extractionId: number;
            extractionType: TExtractionType;
        }[];
        projectId: number;
        databaseId: number;
    }) => void;
    showAnnualizationExportModal: ({
        projectId,
        databaseId,
        startDate,
        endDate,
        interval,
        annualizationType,
    }: {
        projectId: number;
        databaseId: number;
        startDate: string;
        endDate: string;
        interval: "month" | "quarter" | "half" | "year";
        annualizationType: TAnnualizationType;
        reconciliation?: boolean;
    }) => void;
    cancelExport: () => void;
};

export type TSliceExport = TStateExport &
    TActionsPublicExport &
    TDerivedStateExport;

export const initialStateExport = {
    exportModalIsVisible: false,
    exportConfig: {
        exportFormat: "proda",
        prodaIncludeSummary: true,
        prodaIncludeDetailedSummary: false,
        prodaIncludeMappings: true,
        includeNonOperating: false,
    },
    exportData: null,
    exportProjectId: null,
    exportDatabaseId: null,
    nonOperatingAccountsInCoa: false,
    subcategoryInCoa: true,
} as const satisfies TStateExport;

export const createSliceExport: TCreateSlice<TSliceExport> =
    () => (set, get) => {
        const loadExportConfig: TActionsPublicExport["loadExportConfig"] = ({
            exportConfig,
        }) => {
            let newExportConfig: Partial<TExportConfig> = {};
            if (exportConfig.exportFormat == "proda") {
                newExportConfig = {
                    exportFormat: exportConfig.exportFormat,
                    prodaIncludeSummary: exportConfig.includeSummary,
                    prodaIncludeDetailedSummary:
                        exportConfig.includeDetailedSummary,
                    prodaIncludeMappings: exportConfig.includeMappings,
                    includeNonOperating: exportConfig.includeNonOperating,
                };
            } else if (exportConfig.exportFormat == "compat") {
                newExportConfig = {
                    exportFormat: exportConfig.exportFormat,
                    includeNonOperating: exportConfig.includeNonOperating,
                };
            }
            set(
                state => ({
                    exportConfig: {
                        ...state.exportConfig,
                        ...newExportConfig,
                    },
                }),
                false,
                "export/loadExportConfig",
            );
        };
        const updateExportConfig: TActionsPublicExport["updateExportConfig"] =
            ({ exportConfigUpdate }) =>
                set(
                    state => ({
                        exportConfig: {
                            ...state.exportConfig,
                            ...exportConfigUpdate,
                        },
                    }),
                    false,
                    "export/updateExportConfig",
                );
        const showExportModal: TActionsPublicExport["showExportModal"] = ({
            extractions,
            projectId,
            databaseId,
        }) =>
            set(
                {
                    exportModalIsVisible: true,
                    exportProjectId: projectId,
                    exportDatabaseId: databaseId,
                    exportData: {
                        tag: "extractions",
                        extractions,
                    },
                },
                false,
                "export/showExportModal",
            );
        const showAnnualizationExportModal: TActionsPublicExport["showAnnualizationExportModal"] =
            ({
                projectId,
                databaseId,
                startDate,
                endDate,
                interval,
                annualizationType,
                reconciliation,
            }) =>
                set(
                    {
                        exportModalIsVisible: true,
                        exportProjectId: projectId,
                        exportDatabaseId: databaseId,
                        exportData: {
                            tag: "annualization",
                            projectId,
                            databaseId,
                            startDate,
                            endDate,
                            interval,
                            annualizationType,
                            ...(reconciliation === true
                                ? { reconciliation: true }
                                : {}),
                        },
                    },
                    false,
                    "export/showAnnualizationExportModal",
                );
        const cancelExport: TActionsPublicExport["cancelExport"] = () =>
            set(
                {
                    exportModalIsVisible: false,
                    exportData: null,
                    exportProjectId: null,
                    exportDatabaseId: null,
                },
                false,
                "export/cancelExport",
            );
        const actionsPublic: TActionsPublicExport = {
            loadExportConfig,
            updateExportConfig,
            showExportModal,
            showAnnualizationExportModal,
            cancelExport,
        };
        const derivedState: TDerivedStateExport = {
            typedExportConfig: (reconciliation = false) => {
                const {
                    exportFormat,
                    prodaIncludeSummary,
                    prodaIncludeDetailedSummary,
                    prodaIncludeMappings,
                    includeNonOperating,
                } = get().exportConfig;
                if (reconciliation || exportFormat === "proda")
                    return {
                        exportFormat: "proda",
                        includeSummary: prodaIncludeSummary,
                        includeDetailedSummary: prodaIncludeDetailedSummary,
                        includeMappings:
                            !reconciliation && prodaIncludeMappings,
                        includeNonOperating,
                    };
                if (exportFormat === "compat")
                    return { exportFormat, includeNonOperating };
                throw new Error("Unknown export format");
            },
        };

        return {
            ...initialStateExport,
            ...actionsPublic,
            ...derivedState,
        };
    };
